/**
 * Shared presentation helpers for the Conversations surface (ENG-35807),
 * matching the design-system "Dashboard/UI/Agent Ops/Conversations" fixture:
 * channel glyphs, the resolved/unresolved status badge (mapped from our run
 * status), avatar initials, and the small meta chips.
 */
import { Badge } from "@cometchat-ai/ui-dashboard";
import {
  AudioLines,
  Globe,
  Mail,
  MessageCircle,
  MessageSquare,
  type LucideIcon,
} from "../../components/icons";
import type { ReactNode } from "react";

import { StatusPill } from "../../components/status-pill";
import { accentForKey } from "../../lib/accent";
import type { RunStatus } from "../api/types";

export type ConvResolution = "resolved" | "unresolved";

const CHANNELS: Record<string, { label: string; icon: LucideIcon }> = {
  messenger: { label: "Messenger", icon: MessageCircle },
  sms: { label: "SMS", icon: MessageSquare },
  voice: { label: "Voice", icon: AudioLines },
  email: { label: "Email", icon: Mail },
  web: { label: "Web", icon: Globe },
};

export function channelMeta(channel: string): { label: string; icon: LucideIcon } {
  return (
    CHANNELS[channel.toLowerCase()] ?? {
      label: channel || "Unknown",
      icon: MessageSquare,
    }
  );
}

/** Channel filter options for the conversations inbox (value → label). */
export const CHANNEL_OPTIONS = Object.entries(CHANNELS).map(([value, m]) => ({
  value,
  label: m.label,
}));

/**
 * Map our run status onto the design's resolved/unresolved. A completed run
 * reads as "resolved"; anything else (failed, handed off, cancelled, still
 * running, or unknown) reads as "unresolved". This is a v1 proxy — there is
 * no dedicated conversation-resolution field yet.
 */
export function resolution(status: RunStatus | null): ConvResolution {
  return status === "completed" ? "resolved" : "unresolved";
}

/**
 * Per-status glyph spec for the Linear-style status icon, ported from the
 * Storybook "Final" conversations reference. `frac` is the inset progress
 * wedge (0 = empty ring); `resolved` swaps to a solid disc + check.
 */
const STATUS_GLYPH: Record<RunStatus, { frac: number; color: string; resolved?: boolean }> = {
  completed: { frac: 1, color: "var(--color-success)", resolved: true },
  running: { frac: 0.55, color: "var(--color-warning)" },
  handed_off: { frac: 0.28, color: "var(--color-accent)" },
  failed: { frac: 0, color: "var(--color-danger)" },
  cancelled: { frac: 0, color: "var(--color-fg-subtle)" },
};

const OPEN_GLYPH = { frac: 0, color: "var(--color-fg-subtle)" } as const;

/**
 * Linear-style status glyph — a thin ring with an inset progress wedge; a
 * "completed" run reads as a solid disc with a check. Ported from the Final
 * reference and driven by our real run status.
 */
export function StatusIcon({
  status,
  className,
  label,
  color,
}: {
  status: RunStatus | null;
  className?: string;
  label?: string;
  /** Override the per-status glyph color (e.g. so a binary Resolved/Unresolved
   *  badge tints its icon to match the pill tone). Defaults to the status hue. */
  color?: string;
}) {
  const g = status ? STATUS_GLYPH[status] : OPEN_GLYPH;
  const fill = color ?? g.color;
  const r = 4;
  const a = 2 * Math.PI * g.frac;
  const x = 8 + r * Math.sin(a);
  const y = 8 - r * Math.cos(a);
  const large = g.frac > 0.5 ? 1 : 0;
  return (
    <svg
      viewBox="0 0 16 16"
      className={className}
      fill="none"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      {"resolved" in g && g.resolved ? (
        <>
          <circle cx="8" cy="8" r="7" fill={fill} />
          <path
            d="M5 8.2l2 2 4-4.4"
            stroke="white"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : (
        <>
          <circle cx="8" cy="8" r="7" stroke={fill} strokeWidth="1.5" />
          {g.frac > 0 ? (
            <path d={`M8 8 L8 ${8 - r} A${r} ${r} 0 ${large} 1 ${x} ${y} Z`} fill={fill} />
          ) : null}
        </>
      )}
    </svg>
  );
}

/** Stable per-person avatar accent derived from the name (shared palette). */
export const convAccent = accentForKey;

export function initials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .map((p) => p[0] ?? "")
    .filter(Boolean);
  return (parts[0] ?? "?").concat(parts[1] ?? "").toUpperCase();
}

export function StatusBadge({ status }: { status: RunStatus | null }) {
  const resolved = resolution(status) === "resolved";
  return (
    <StatusPill tone={resolved ? "success" : "danger"} className="gap-1.5">
      <StatusIcon
        status={status}
        className="h-3 w-3"
        color={resolved ? "var(--color-success)" : "var(--color-danger)"}
      />
      {resolved ? "Resolved" : "Unresolved"}
    </StatusPill>
  );
}

export function MetaBadge({ children }: { children: ReactNode }) {
  return (
    <Badge
      variant="outline"
      className="gap-1 rounded-full border-border bg-muted px-2 py-0.5 text-2xs font-medium text-muted-foreground"
    >
      {children}
    </Badge>
  );
}

export function ChannelBadge({ channel }: { channel: string }) {
  const { label, icon: Icon } = channelMeta(channel);
  return (
    <MetaBadge>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </MetaBadge>
  );
}
